/**
 *
 * App
 *
 * This component is the skeleton around the actual pages, and should only
 * contain code that should be seen on all pages. (e.g. navigation bar)
 */

import { Route, Routes, RouterProvider, createBrowserRouter } from 'react-router-dom';
import { Product } from './pages/Product/Loadable';
import { NotFoundPage } from './pages/NotFoundPage/Loadable';
import { useTranslation } from 'react-i18next';
import { Course } from './pages/Create/Courses/Course/Loadable';
import { Courses } from './pages/Create/Courses/Loadable';
import { Module } from './pages/Create/Modules/Module/Loadable';
import { Modules } from './pages/Create/Modules/Loadable';
import { Preview } from './pages/Create/Preview/Loadable';
import { Group } from './pages/Manage/Groups/Group/Loadable';
import { ActionEditor } from './components/ActionEdit/Loadable';
import { LogIn } from './components/LogIn/Loadable';
import { Create } from './pages/Create/Loadable';
import { Manage } from './pages/Manage/Loadable';
import { Admin } from './pages/Admin/Loadable';
import { Password } from './components/Password/Loadable';
import { PasswordType } from './components/Password';
import { MANAGE_PANELS } from './pages/Manage/Sidebar';
import { Users } from './pages/Manage/Users/Loadable';
import { Groups } from './pages/Manage/Groups/Loadable';
import { ForgotPassword } from './components/ForgotPassword';
import { CourseUsers } from './pages/Create/Courses/Course/Users/Loadable';
import { Theme } from './pages/Manage/Theme/Loadable';
import { FlowPage } from 'core/slices/flow';
import { SignUp } from './components/SignUp/Loadable';
import { New } from './pages/New/Loadable';
import { SECTIONS } from 'constants/sections';
import { Home } from './pages/Home/Loadable';
import { Systems } from './pages/Manage/Systems/Loadable';
import { Certificates } from './pages/Manage/Certificates/Loadable';
import { Variables } from './pages/Manage/Variables/Loadable';
import { CourseTabs } from './pages/Create/Courses/Course/Users';
import { Dashboard } from './pages/Manage/Dashboard';

const router = createBrowserRouter([{ path: '*', Component: Root }]);

export function App() {
  return (
    <>
      <RouterProvider router={router} />
      <video id="videoEl" className="hide-video" playsInline muted webkit-playsinline="true" />
    </>
  );
}

export function Root() {
  const { i18n } = useTranslation();

  return (
    <Routes>
      <Route
        index={true}
        element={
          <Admin>
            <Create section={SECTIONS.MODULES}>
              <CourseUsers />
            </Create>
          </Admin>
        }
      ></Route>

      <Route path={'/'} element={<Admin />}>
        <Route
          index={true}
          element={
            <Create section={SECTIONS.MODULES}>
              <CourseUsers />
            </Create>
          }
        ></Route>
      </Route>

      <Route path={'/dashboard'} element={<Admin />}>
        <Route
          index={true}
          element={
            <Create section={SECTIONS.MODULES}>
              <Dashboard></Dashboard>
            </Create>
          }
        ></Route>
      </Route>

      <Route path={SECTIONS.MODULES} element={<Admin />}>
        <Route
          index={true}
          element={
            <Create section={SECTIONS.MODULES}>
              <Courses />
            </Create>
          }
        ></Route>
      </Route>

      <Route path={SECTIONS.WORKSPACE} element={<Admin />}>
        <Route
          index={true}
          element={
            <Create section={SECTIONS.WORKSPACE}>
              <CourseUsers />
            </Create>
          }
        ></Route>
      </Route>
      <Route path={SECTIONS.GROUPS} element={<Admin />}>
        <Route
          index={true}
          element={
            <Create section={SECTIONS.GROUPS}>
              <Groups />
            </Create>
          }
        ></Route>
      </Route>

      <Route path={SECTIONS.SYSTEMS} element={<Admin />}>
        <Route
          index={true}
          element={
            <Create section={SECTIONS.SYSTEMS}>
              <Systems />
            </Create>
          }
        ></Route>
      </Route>
      <Route path={SECTIONS.CERTIFICATES} element={<Admin />}>
        <Route
          index={true}
          element={
            <Create section={SECTIONS.CERTIFICATES}>
              <Certificates />
            </Create>
          }
        ></Route>
      </Route>
      <Route path={SECTIONS.VARIABLES} element={<Admin />}>
        <Route
          index={true}
          element={
            <Create section={SECTIONS.VARIABLES}>
              <Variables />
            </Create>
          }
        ></Route>
        <Route
          path={`:variableId`}
          element={
            <Create section={SECTIONS.VARIABLES}>
              <Variables />
            </Create>
          }
        />
      </Route>
      <Route path={SECTIONS.ADMINISTRATORS} element={<Admin />}>
        <Route
          index={true}
          element={
            <Create section={SECTIONS.ADMINISTRATORS}>
              <Users />
            </Create>
          }
        ></Route>
      </Route>
      <Route path={SECTIONS.THEME} element={<Admin />}>
        <Route
          index={true}
          element={
            <Create section={SECTIONS.THEME}>
              <Theme />
            </Create>
          }
        ></Route>
      </Route>
      <Route path={'home'} element={<Home />} />
      <Route path={'manager'} element={<LogIn />} />

      <Route path={'module'} element={<Admin />}>
        <Route path={`:moduleId`} element={<Module />} />
        <Route path={`:moduleId/preview`} element={<Preview />} />
        <Route path={`:moduleId/:actionId`} element={<Preview edit={true} />} />
        <Route path={`:moduleId/:actionId/preview`} element={<Preview />} />
        <Route path={`:moduleId/s/:sceneId`} element={<ActionEditor />} />
      </Route>

      <Route path={SECTIONS.COURSES} element={<Admin />}>
        <Route
          index={true}
          element={
            <Create section={SECTIONS.COURSES}>
              <Courses isDefault={true} />
            </Create>
          }
        ></Route>
      </Route>

      <Route path={SECTIONS.COURSE} element={<Admin />}>
        <Route
          index={true}
          element={
            <Create section={SECTIONS.COURSES}>
              <Courses isDefault={true} />
            </Create>
          }
        />
        <Route path={`:courseId`} element={<CourseUsers />} />
        <Route path={`:courseId/workflow`} element={<Course />} />
        <Route path={`:courseId/modules`} element={<CourseUsers tab={CourseTabs.MODULES} />} />
        <Route path={`:courseId/learners`} element={<CourseUsers tab={CourseTabs.LEARNERS} />} />
        <Route path={`:courseId/records`} element={<CourseUsers tab={CourseTabs.RECORDS} />} />
        <Route path={`:courseId/notification`} element={<CourseUsers tab={CourseTabs.NOTIFICATION} />} />
        <Route path={`:courseId/publish`} element={<CourseUsers tab={CourseTabs.PUBLISH} />} />
        <Route path={`:courseId/editors`} element={<CourseUsers tab={CourseTabs.EDITORS} />} />
        <Route path={`:courseId/:nodeId`} element={<Course />} />
        <Route path={`:courseId/preview`} element={<Preview />} />
        <Route path={`:courseId/:nodeId/preview`} element={<Preview />} />
        <Route path={`:courseId/module/`}>
          <Route path={`:moduleId`} element={<Module page={FlowPage.MOD_CAROUSEL} />} />
          <Route path={`:moduleId/cards`} element={<Module page={FlowPage.MOD_CAROUSEL} />} />
          <Route path={`:moduleId/cards/preview`} element={<Preview page={FlowPage.MOD_CAROUSEL} />} />
          <Route path={`:moduleId/cards/:actionId`} element={<Preview page={FlowPage.MOD_CAROUSEL} edit={true} />} />
          <Route path={`:moduleId/cards/:actionId/preview`} element={<Preview page={FlowPage.MOD_CAROUSEL} />} />
          <Route path={`:moduleId/storyboard`} element={<Module page={FlowPage.MOD_FLOW} />} />
          <Route path={`:moduleId/storyboard/preview`} element={<Preview page={FlowPage.MOD_FLOW} />} />
          <Route path={`:moduleId/storyboard/:actionId`} element={<Preview page={FlowPage.MOD_FLOW} edit={true} />} />
          <Route path={`:moduleId/storyboard/:actionId/preview`} element={<Preview page={FlowPage.MOD_FLOW} />} />
          <Route path={`:moduleId/preview`} element={<Preview />} />
          <Route path={`:moduleId/:actionId`} element={<Preview edit={true} />} />
          <Route path={`:moduleId/:actionId/preview`} element={<Preview />} />
          <Route path={`:moduleId/s/:sceneId`} element={<ActionEditor />} />
        </Route>
        <Route path={`:courseId/scene/:sceneId`} element={<ActionEditor />} />
      </Route>
      <Route
        path={'folders'}
        element={
          <Create section={SECTIONS.MODULES}>
            <Modules />
          </Create>
        }
      />

      <Route path={'theme'} element={<Admin />}>
        <Route
          index={true}
          element={
            <Manage panel={MANAGE_PANELS.OVERVIEW}>
              <Theme />
            </Manage>
          }
        />
      </Route>
      <Route path={'admin'} element={<Admin />}>
        <Route
          index={true}
          element={
            <Manage panel={MANAGE_PANELS.USERS}>
              <Users />
            </Manage>
          }
        />
        <Route
          path={'groups'}
          element={
            <Manage panel={MANAGE_PANELS.GROUPS}>
              <Groups />
            </Manage>
          }
        />
        <Route
          path={'group/:groupId'}
          element={
            <Manage panel={MANAGE_PANELS.GROUPS}>
              <Group />
            </Manage>
          }
        />
      </Route>

      <Route path={`go/:token`} element={<Product />} />
      <Route path={`token/:token`} element={<Product />} />
      <Route path={`go/:token/:start`} element={<Product />} />
      <Route path={`en/:token`} element={<Product />} />
      <Route path={`scorm/:lang/:token`} element={<Product scorm={true} />} />
      <Route path={`en/:token/:entityToken`} element={<Product />} />
      <Route path={`invite/:inviteToken`} element={<Product />} />
      <Route path={`sso/:inviteToken/:token`} element={<Product useToken={true} />} />
      <Route path={`invite/:inviteToken/:entityToken`} element={<Product useToken={true} />} />
      <Route path={`token/:token/:entityToken`} element={<Product />} />
      <Route path={`hello/:token`} element={<Password type={PasswordType.RESET} />} />
      <Route path={`hello/:token/:returnToken`} element={<Password type={PasswordType.RESET} />} />
      <Route path={`reset/:token`} element={<Password type={PasswordType.RESET} />} />
      <Route path={`forgot`} element={<ForgotPassword />} />
      <Route path={`signup`} element={<SignUp />} />
      <Route path={'new/:token'} element={<New />} />
      <Route path={'new/:token/:courseId/:moduleId'} element={<New />} />
      <Route element={<NotFoundPage />} />
    </Routes>
  );
}
